import { ConfigFormHeader } from '@/components/ConfigFormHeader'
import { useDealership } from '@/contexts/DealershipContext'
import { saveConfiguration } from '@/services/configuration.api'
import { RemindersFollowUpFormValues, RemindersFollowUpSectionData } from '@/types/configuration'
import { Alert, Card, Divider, Group, NumberInput, Stack, Switch, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'

const REMINDER_2_MAX = 600
const REMINDER_1_MIN = 1440
const REMINDER_1_MAX = 43200
const PROXIMITY_DEFAULT = 120

export interface RemindersFollowUpFormProps {
  /** Populated by the parent from `appointment_recovery`, `showroom_unsold_recovery`, and `appointment_reminders` */
  data: RemindersFollowUpSectionData | null
}

export function RemindersFollowUpForm({ data }: RemindersFollowUpFormProps) {
  const { selectedEntity } = useDealership()
  const dealershipId = selectedEntity?.id || ''
  const configsLoadedRef = useRef(false)

  const form = useForm<RemindersFollowUpFormValues>({
    initialValues: {
      missedAppointmentRecovery: {
        enabled: false,
        touch1DelayHours: 0,
        touch1DelayMinutes: 45,
        touch2DelayHours: 24,
        touch2DelayMinutes: 0,
      },
      showroomUnsoldRecovery: {
        enabled: false,
        touch1DelayHours: 12,
        touch2DelayDays: 3,
      },
      appointmentReminders: {
        enabled: false,
        dayBeforeReminder: {
          enabled: true,
          offsetMinutes: 24 * 60,
        },
        hourBeforeReminder: {
          enabled: true,
          offsetMinutes: 60,
        },
        respectQuietHours: true,
        proximityTime: {
          enabled: false,
          offsetMinutes: PROXIMITY_DEFAULT,
        },
      },
    },
  })

  const [firstReminderHoursError, setFirstReminderHoursError] = useState<string | null>(null)
  const [firstReminderMinutesError, setFirstReminderMinutesError] = useState<string | null>(null)
  const [sameDayReminderHoursError, setSameDayReminderHoursError] = useState<string | null>(null)
  const [sameDayReminderMinutesError, setSameDayReminderMinutesError] = useState<string | null>(null)
  const [proximityHoursError, setProximityHoursError] = useState<string | null>(null)
  const [proximityMinutesError, setProximityMinutesError] = useState<string | null>(null)
  const [isRemindersSaving, setIsRemindersSaving] = useState(false)

  // Sync from parent (DealershipConfiguration loads the three API configs in parallel)
  useEffect(() => {
    if (!data) {
      setFirstReminderHoursError(null)
      setFirstReminderMinutesError(null)
      setSameDayReminderHoursError(null)
      setSameDayReminderMinutesError(null)
      setProximityHoursError(null)
      setProximityMinutesError(null)
    }

    if (data === null) {
      form.setFieldValue('missedAppointmentRecovery', {
        enabled: false,
        touch1DelayHours: 0,
        touch1DelayMinutes: 45,
        touch2DelayHours: 24,
        touch2DelayMinutes: 0,
      })
      form.setFieldValue('showroomUnsoldRecovery', {
        enabled: false,
        touch1DelayHours: 12,
        touch2DelayDays: 3,
      })
      form.setFieldValue('appointmentReminders', {
        enabled: false,
        dayBeforeReminder: { enabled: true, offsetMinutes: 24 * 60 },
        hourBeforeReminder: { enabled: true, offsetMinutes: 60 },
        respectQuietHours: true,
        proximityTime: { enabled: false, offsetMinutes: PROXIMITY_DEFAULT },
      })
      return
    }

    setFirstReminderHoursError(null)
    setFirstReminderMinutesError(null)
    setSameDayReminderHoursError(null)
    setSameDayReminderMinutesError(null)
    setProximityHoursError(null)
    setProximityMinutesError(null)

    if (data.appointmentRecovery) {
      const apiConfig = data.appointmentRecovery as {
        timing?: { touch1DelayMinutes?: number; touch2DelayHours?: number }
        enabled?: boolean
      }
      const touch1TotalMinutes = apiConfig.timing?.touch1DelayMinutes ?? 45
      form.setFieldValue('missedAppointmentRecovery', {
        enabled: apiConfig.enabled ?? false,
        touch1DelayHours: Math.floor(touch1TotalMinutes / 60),
        touch1DelayMinutes: touch1TotalMinutes % 60,
        touch2DelayHours: apiConfig.timing?.touch2DelayHours ?? 24,
        touch2DelayMinutes: 0,
      })
    } else {
      form.setFieldValue('missedAppointmentRecovery', {
        enabled: false,
        touch1DelayHours: 0,
        touch1DelayMinutes: 45,
        touch2DelayHours: 24,
        touch2DelayMinutes: 0,
      })
    }

    if (data.showroomUnsoldRecovery) {
      const apiConfig = data.showroomUnsoldRecovery as {
        enabled?: boolean
        timing?: { touch1DelayHours?: number; touch2DelayDays?: number }
      }
      form.setFieldValue('showroomUnsoldRecovery', {
        enabled: apiConfig.enabled ?? false,
        touch1DelayHours: apiConfig.timing?.touch1DelayHours ?? 12,
        touch2DelayDays: apiConfig.timing?.touch2DelayDays ?? 3,
      })
    } else {
      form.setFieldValue('showroomUnsoldRecovery', {
        enabled: false,
        touch1DelayHours: 12,
        touch2DelayDays: 3,
      })
    }

    if (data.appointmentReminders) {
      const ar = data.appointmentReminders as {
        enabled?: boolean
        respectQuietHours?: boolean
        dayBeforeReminder?: { enabled?: boolean; offsetMinutes?: number }
        hourBeforeReminder?: { enabled?: boolean; offsetMinutes?: number }
        proximityTime?: { enabled?: boolean; offsetMinutes?: number }
      }
      form.setFieldValue('appointmentReminders', {
        enabled: ar.enabled ?? false,
        respectQuietHours: ar.respectQuietHours ?? true,
        dayBeforeReminder: {
          enabled: ar.dayBeforeReminder?.enabled ?? true,
          offsetMinutes: ar.dayBeforeReminder?.offsetMinutes ?? REMINDER_1_MIN,
        },
        hourBeforeReminder: {
          enabled: ar.hourBeforeReminder?.enabled ?? true,
          offsetMinutes: ar.hourBeforeReminder?.offsetMinutes ?? 60,
        },
        proximityTime: {
          enabled: ar.proximityTime?.enabled ?? false,
          offsetMinutes: ar.proximityTime?.offsetMinutes ?? PROXIMITY_DEFAULT,
        },
      })
    } else {
      form.setFieldValue('appointmentReminders', {
        enabled: false,
        dayBeforeReminder: { enabled: true, offsetMinutes: 24 * 60 },
        hourBeforeReminder: { enabled: true, offsetMinutes: 60 },
        respectQuietHours: true,
        proximityTime: { enabled: false, offsetMinutes: PROXIMITY_DEFAULT },
      })
    }
  }, [data])

  const recoveryEnabled = form.values.missedAppointmentRecovery?.enabled ?? false
  const showroomUnsoldEnabled = form.values.showroomUnsoldRecovery?.enabled ?? false
  const appointmentRemindersEnabled = form.values.appointmentReminders?.enabled ?? false
  const firstOffsetMinutes = form.values.appointmentReminders?.dayBeforeReminder?.offsetMinutes ?? REMINDER_1_MIN
  const firstReminderHoursFloor = Math.floor(firstOffsetMinutes / 60)
  const firstReminderMinutesMax = Math.min(59, Math.max(0, REMINDER_1_MAX - firstReminderHoursFloor * 60))

  const sameDayOffsetMinutes = form.values.appointmentReminders?.hourBeforeReminder?.offsetMinutes ?? 60
  const sameDayHoursFloor = Math.floor(sameDayOffsetMinutes / 60)
  const sameDayMinutesMax = Math.min(59, Math.max(0, REMINDER_2_MAX - sameDayHoursFloor * 60))

  const proximityTimeEnabled = form.values.appointmentReminders?.proximityTime?.enabled ?? false
  const proximityOffsetMinutes = form.values.appointmentReminders?.proximityTime?.offsetMinutes ?? PROXIMITY_DEFAULT
  const proximityHoursFloor = Math.floor(proximityOffsetMinutes / 60)
  const proximityMinutesMax = 59

  // Auto-convert minutes > 59 to hours for Touch 1
  const handleTouch1MinutesChange = (value: string | number) => {
    const minutes = typeof value === 'string' ? parseInt(value, 10) : value
    if (isNaN(minutes)) return

    if (minutes >= 60) {
      const extraHours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      const currentHours = form.values.missedAppointmentRecovery?.touch1DelayHours || 0
      form.setFieldValue('missedAppointmentRecovery.touch1DelayHours', currentHours + extraHours)
      form.setFieldValue('missedAppointmentRecovery.touch1DelayMinutes', remainingMinutes)
    } else {
      form.setFieldValue('missedAppointmentRecovery.touch1DelayMinutes', minutes)
    }
  }

  // Auto-convert minutes > 59 to hours for Touch 2
  const handleTouch2MinutesChange = (value: string | number) => {
    const minutes = typeof value === 'string' ? parseInt(value, 10) : value
    if (isNaN(minutes)) return

    if (minutes >= 60) {
      const extraHours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      const currentHours = form.values.missedAppointmentRecovery?.touch2DelayHours || 0
      form.setFieldValue('missedAppointmentRecovery.touch2DelayHours', currentHours + extraHours)
      form.setFieldValue('missedAppointmentRecovery.touch2DelayMinutes', remainingMinutes)
    } else {
      form.setFieldValue('missedAppointmentRecovery.touch2DelayMinutes', minutes)
    }
  }

  const handleSubmit = async (values: RemindersFollowUpFormValues) => {
    const { appointmentReminders, missedAppointmentRecovery, showroomUnsoldRecovery } = values

    const firstOffset = appointmentReminders?.dayBeforeReminder?.offsetMinutes
    if (
      appointmentReminders?.enabled &&
      typeof firstOffset === 'number' &&
      (firstOffset < REMINDER_1_MIN || firstOffset > REMINDER_1_MAX)
    ) {
      notifications.show({
        title: 'Reminder 1',
        message: 'Lead time must be at least 24 hours and at most 30 days before the appointment.',
        color: 'red',
      })
      return
    }

    const sameDayOffset = appointmentReminders?.hourBeforeReminder?.offsetMinutes
    if (appointmentReminders?.enabled && typeof sameDayOffset === 'number' && sameDayOffset > REMINDER_2_MAX) {
      notifications.show({
        title: 'Same Day Reminder',
        message: 'Lead time cannot be more than 10 hours before the appointment.',
        color: 'red',
      })
      return
    }

    if (appointmentReminders?.enabled && dealershipId && (firstReminderHoursError || firstReminderMinutesError)) {
      notifications.show({
        title: 'Reminder 1',
        message: 'Fix the hours and minutes before saving.',
        color: 'red',
      })
      return
    }

    if (appointmentReminders?.enabled && dealershipId && (sameDayReminderHoursError || sameDayReminderMinutesError)) {
      notifications.show({
        title: 'Same Day Reminder',
        message: 'Fix the hours and minutes before saving.',
        color: 'red',
      })
      return
    }

    const proximityTotal = appointmentReminders?.proximityTime?.offsetMinutes
    if (
      appointmentReminders?.enabled &&
      appointmentReminders?.proximityTime?.enabled &&
      typeof proximityTotal === 'number' &&
      proximityTotal < 1
    ) {
      notifications.show({
        title: 'Booking-to-Reminder proximity',
        message: 'Threshold must be at least 1 minute.',
        color: 'red',
      })
      return
    }

    if (
      appointmentReminders?.enabled &&
      appointmentReminders?.proximityTime?.enabled &&
      dealershipId &&
      (proximityHoursError || proximityMinutesError)
    ) {
      notifications.show({
        title: 'Booking-to-Reminder proximity',
        message: 'Fix the hours and minutes before saving.',
        color: 'red',
      })
      return
    }

    if (!dealershipId) {
      return
    }

    setIsRemindersSaving(true)
    let hasError = false
    const fail = (label: string, err: unknown) => {
      hasError = true
      console.error(`Failed to save ${label}:`, err)
    }

    try {
      if (missedAppointmentRecovery) {
        try {
          const appointmentRecoveryConfig = {
            enabled: missedAppointmentRecovery.enabled,
            timing: {
              touch1DelayMinutes:
                (missedAppointmentRecovery.touch1DelayHours ?? 0) * 60 + (missedAppointmentRecovery.touch1DelayMinutes ?? 0),
              touch2DelayHours: missedAppointmentRecovery.touch2DelayHours ?? 24,
              respectQuietHours: true,
              respectEndOfDay: true,
              skipHolidays: true,
            },
            businessLogic: {
              maxRecoveryAttempts: 2,
              pauseOnManualMessage: true,
              stopOnLateArrival: true,
            },
          }
          const response = await saveConfiguration(dealershipId, 'appointment_recovery', appointmentRecoveryConfig)
          if (!response.success) {
            fail('missed appointment recovery', response.error)
            notifications.show({
              title: 'Error',
              message: response.error || 'Failed to save missed appointment recovery',
              color: 'red',
            })
          }
        } catch (e) {
          fail('missed appointment recovery', e)
          notifications.show({ title: 'Error', message: 'Failed to save missed appointment recovery', color: 'red' })
        }
      }

      if (showroomUnsoldRecovery) {
        try {
          const showroomUnsoldRecoveryConfig = {
            enabled: showroomUnsoldRecovery.enabled ?? false,
            timing: {
              touch1DelayHours: showroomUnsoldRecovery.touch1DelayHours ?? 12,
              touch2DelayDays: showroomUnsoldRecovery.touch2DelayDays ?? 3,
              respectQuietHours: true,
              respectBusinessHours: true,
              skipHolidays: true,
            },
            businessLogic: {
              maxRecoveryAttempts: 2,
              pauseOnManualMessage: true,
              stopOnCustomerReply: true,
              stopOnNewAppointment: true,
              stopOnOptOut: true,
            },
          }
          const response = await saveConfiguration(dealershipId, 'showroom_unsold_recovery', showroomUnsoldRecoveryConfig)
          if (!response.success) {
            fail('showroom follow-up', response.error)
            notifications.show({
              title: 'Error',
              message: response.error || 'Failed to save showroom unsold follow-up',
              color: 'red',
            })
          } else {
            configsLoadedRef.current = true
          }
        } catch (e) {
          fail('showroom follow-up', e)
          notifications.show({ title: 'Error', message: 'Failed to save showroom unsold follow-up', color: 'red' })
        }
      }

      if (appointmentReminders) {
        try {
          const response = await saveConfiguration(dealershipId, 'appointment_reminders', appointmentReminders)
          if (!response.success) {
            fail('appointment reminders', response.error)
            notifications.show({
              title: 'Error',
              message: response.error || 'Failed to save appointment reminders',
              color: 'red',
            })
          } else {
            configsLoadedRef.current = true
          }
        } catch (e) {
          fail('appointment reminders', e)
          notifications.show({ title: 'Error', message: 'Failed to save appointment reminders', color: 'red' })
        }
      }
    } finally {
      setIsRemindersSaving(false)
    }

    if (!hasError) {
      notifications.show({
        title: 'Success',
        message: 'Reminders and follow-up settings saved',
        color: 'green',
      })
    }
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <ConfigFormHeader
          onReset={() => {
            setFirstReminderHoursError(null)
            setFirstReminderMinutesError(null)
            setSameDayReminderHoursError(null)
            setSameDayReminderMinutesError(null)
            setProximityHoursError(null)
            setProximityMinutesError(null)
            form.reset()
          }}
          isSaving={isRemindersSaving}
        />
        {/* Missed Appointment Recovery */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={600}>Missed Appointment Recovery</Text>
                <Text size="sm" c="dimmed">
                  Automatically reach out to customers who missed their appointments
                </Text>
              </div>
              <Switch
                size="lg"
                onLabel="ON"
                offLabel="OFF"
                checked={recoveryEnabled}
                onChange={event => form.setFieldValue('missedAppointmentRecovery.enabled', event.currentTarget.checked)}
              />
            </Group>

            {!recoveryEnabled && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                When disabled, no recovery messages will be sent for missed appointments.
              </Alert>
            )}

            <Divider my="xs" />

            {/* Touch 1 Delay */}
            <div style={{ opacity: recoveryEnabled ? 1 : 0.5 }}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Touch 1 Delay (First Outreach)
                </Text>
                <Text size="xs" c="blue">
                  Default: 45 minutes
                </Text>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                Time after a no-show is detected and recovery starts before sending the first message
              </Text>
              <Group gap="sm" align="flex-end">
                <NumberInput
                  label="Hours"
                  min={0}
                  max={2}
                  disabled={!recoveryEnabled}
                  w={80}
                  styles={{ input: { textAlign: 'center' } }}
                  value={form.values.missedAppointmentRecovery?.touch1DelayHours || 0}
                  onChange={value =>
                    form.setFieldValue('missedAppointmentRecovery.touch1DelayHours', typeof value === 'number' ? value : 0)
                  }
                />
                <NumberInput
                  label="Minutes"
                  min={0}
                  max={59}
                  disabled={!recoveryEnabled}
                  w={80}
                  styles={{ input: { textAlign: 'center' } }}
                  value={form.values.missedAppointmentRecovery?.touch1DelayMinutes || 0}
                  onChange={handleTouch1MinutesChange}
                />
                <Text size="sm" c="dimmed" pb={8}>
                  Recommended: 30-90 minutes
                </Text>
              </Group>
            </div>

            <Divider my="xs" />

            {/* Touch 2 Delay */}
            <div style={{ opacity: recoveryEnabled ? 1 : 0.5 }}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Touch 2 Delay (Follow-up)
                </Text>
                <Text size="xs" c="blue">
                  Default: 24 hours
                </Text>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                Time after Touch 1 before sending the follow-up message (if no response)
              </Text>
              <Group gap="sm" align="flex-end">
                <NumberInput
                  label="Hours"
                  min={0}
                  max={72}
                  disabled={!recoveryEnabled}
                  w={80}
                  styles={{ input: { textAlign: 'center' } }}
                  value={form.values.missedAppointmentRecovery?.touch2DelayHours || 0}
                  onChange={value =>
                    form.setFieldValue('missedAppointmentRecovery.touch2DelayHours', typeof value === 'number' ? value : 0)
                  }
                />
                <NumberInput
                  label="Minutes"
                  min={0}
                  max={59}
                  disabled={!recoveryEnabled}
                  w={80}
                  styles={{ input: { textAlign: 'center' } }}
                  value={form.values.missedAppointmentRecovery?.touch2DelayMinutes || 0}
                  onChange={handleTouch2MinutesChange}
                />
                <Text size="sm" c="dimmed" pb={8}>
                  Recommended: 12-48 hours
                </Text>
              </Group>
            </div>
          </Stack>
        </Card>

        {/* Showroom Unsold Recovery */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={600}>Showroom Unsold Follow-up</Text>
                <Text size="sm" c="dimmed">
                  Automatically follow up with customers who visited but didn't purchase
                </Text>
              </div>
              <Switch
                size="lg"
                onLabel="ON"
                offLabel="OFF"
                checked={showroomUnsoldEnabled}
                onChange={event => form.setFieldValue('showroomUnsoldRecovery.enabled', event.currentTarget.checked)}
              />
            </Group>

            {!showroomUnsoldEnabled && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                When disabled, no follow-up messages will be sent to showroom visitors who didn't purchase.
              </Alert>
            )}

            <Divider my="xs" />

            {/* Touch 1 Delay */}
            <div style={{ opacity: showroomUnsoldEnabled ? 1 : 0.5 }}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Touch 1 Delay (Day 0-1 Check-in)
                </Text>
                <Text size="xs" c="blue">
                  Default: 12 hours
                </Text>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                Time after showroom visit before sending the first follow-up message
              </Text>
              <Group gap="sm" align="flex-end">
                <NumberInput
                  label="Hours"
                  min={0}
                  max={24}
                  disabled={!showroomUnsoldEnabled}
                  w={80}
                  styles={{ input: { textAlign: 'center' } }}
                  value={form.values.showroomUnsoldRecovery?.touch1DelayHours ?? 12}
                  onChange={value =>
                    form.setFieldValue('showroomUnsoldRecovery.touch1DelayHours', typeof value === 'number' ? value : 12)
                  }
                />
                <Text size="sm" c="dimmed" pb={8}>
                  Recommended: 6-24 hours
                </Text>
              </Group>
            </div>

            <Divider my="xs" />

            {/* Touch 2 Delay */}
            <div style={{ opacity: showroomUnsoldEnabled ? 1 : 0.5 }}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Touch 2 Delay (Day 3-5 Follow-up)
                </Text>
                <Text size="xs" c="blue">
                  Default: 3 days
                </Text>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                Days after Touch 1 before sending the follow-up message (if no response)
              </Text>
              <Group gap="sm" align="flex-end">
                <NumberInput
                  label="Days"
                  min={0}
                  max={7}
                  disabled={!showroomUnsoldEnabled}
                  w={80}
                  styles={{ input: { textAlign: 'center' } }}
                  value={form.values.showroomUnsoldRecovery?.touch2DelayDays ?? 3}
                  onChange={value =>
                    form.setFieldValue('showroomUnsoldRecovery.touch2DelayDays', typeof value === 'number' ? value : 3)
                  }
                />
                <Text size="sm" c="dimmed" pb={8}>
                  Recommended: 2-7 days
                </Text>
              </Group>
            </div>
          </Stack>
        </Card>

        {/* Appointment Reminders */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={600}>Appointment Reminders</Text>
                <Text size="sm" c="dimmed">
                  Automatically send reminders before appointments
                </Text>
              </div>
              <Switch
                size="lg"
                onLabel="ON"
                offLabel="OFF"
                checked={appointmentRemindersEnabled}
                onChange={event => {
                  const isEnabled = event.currentTarget.checked
                  form.setFieldValue('appointmentReminders.enabled', isEnabled)

                  if (!isEnabled) {
                    setFirstReminderHoursError(null)
                    setFirstReminderMinutesError(null)
                    setSameDayReminderHoursError(null)
                    setSameDayReminderMinutesError(null)
                    setProximityHoursError(null)
                    setProximityMinutesError(null)
                  }

                  if (isEnabled) {
                    const ar = form.values.appointmentReminders
                    form.setFieldValue('appointmentReminders.dayBeforeReminder', {
                      enabled: ar?.dayBeforeReminder?.enabled ?? true,
                      offsetMinutes: ar?.dayBeforeReminder?.offsetMinutes ?? REMINDER_1_MIN,
                    })
                    form.setFieldValue('appointmentReminders.hourBeforeReminder', {
                      enabled: ar?.hourBeforeReminder?.enabled ?? true,
                      offsetMinutes: ar?.hourBeforeReminder?.offsetMinutes ?? 60,
                    })
                    form.setFieldValue('appointmentReminders.respectQuietHours', ar?.respectQuietHours ?? true)
                    form.setFieldValue('appointmentReminders.proximityTime', {
                      enabled: ar?.proximityTime?.enabled ?? false,
                      offsetMinutes: ar?.proximityTime?.offsetMinutes ?? PROXIMITY_DEFAULT,
                    })
                  }
                }}
              />
            </Group>

            {!appointmentRemindersEnabled && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                When disabled, no reminder messages will be sent before appointments.
              </Alert>
            )}

            {appointmentRemindersEnabled && (
              <Stack gap="sm" mt="xs">
                <Text size="sm" fw={500}>
                  Reminder 1
                </Text>
                <Text size="xs" c="dimmed">
                  Time before appointment the first reminder message should be sent
                </Text>
                <Group gap="sm" align="flex-start">
                  <NumberInput
                    label="Hours"
                    min={24}
                    max={720}
                    w={110}
                    styles={{ input: { textAlign: 'center' } }}
                    error={firstReminderHoursError}
                    value={firstReminderHoursFloor}
                    onChange={value => {
                      if (firstReminderMinutesError) {
                        return
                      }

                      if (typeof value === 'number' && !Number.isNaN(value)) {
                        if (value < 24) {
                          setFirstReminderHoursError('Hours must be at least 24')
                          return
                        }
                        if (value > 720) {
                          setFirstReminderHoursError('Hours cannot be more than 30 days (720 hours)')
                          return
                        }
                        if (!Number.isInteger(value)) {
                          setFirstReminderHoursError('Hours must be a whole number')
                          return
                        }
                        const minutes = (form.values.appointmentReminders?.dayBeforeReminder?.offsetMinutes ?? 24 * 60) % 60
                        const total = value * 60 + minutes
                        if (total < REMINDER_1_MIN) {
                          setFirstReminderHoursError('Lead time must be at least 24 hours')
                          return
                        }
                        if (total > REMINDER_1_MAX) {
                          setFirstReminderHoursError('Lead time cannot exceed 30 days')
                          return
                        }
                        setFirstReminderHoursError(null)
                        form.setFieldValue(
                          'appointmentReminders.dayBeforeReminder.offsetMinutes',
                          Math.round(Math.min(total, REMINDER_1_MAX)),
                        )
                        return
                      }

                      if (firstReminderHoursError) {
                        return
                      }

                      setFirstReminderHoursError(null)
                      const minutes = (form.values.appointmentReminders?.dayBeforeReminder?.offsetMinutes ?? 24 * 60) % 60
                      const total = 24 * 60 + minutes
                      form.setFieldValue(
                        'appointmentReminders.dayBeforeReminder.offsetMinutes',
                        Math.max(REMINDER_1_MIN, Math.round(total)),
                      )
                    }}
                  />
                  <NumberInput
                    label="Minutes"
                    min={0}
                    max={firstReminderMinutesMax}
                    w={110}
                    styles={{ input: { textAlign: 'center' } }}
                    error={firstReminderMinutesError}
                    value={firstOffsetMinutes % 60}
                    onChange={value => {
                      if (firstReminderHoursError) {
                        return
                      }

                      if (typeof value === 'number' && !Number.isNaN(value)) {
                        if (value < 0 || value > firstReminderMinutesMax) {
                          setFirstReminderMinutesError(
                            firstReminderMinutesMax < 59
                              ? `Minutes must be between 0 and ${firstReminderMinutesMax} at this hour count`
                              : 'Minutes must be between 0 and 59',
                          )
                          return
                        }
                        if (!Number.isInteger(value)) {
                          setFirstReminderMinutesError('Minutes must be a whole number')
                          return
                        }
                        const hours = Math.floor(
                          (form.values.appointmentReminders?.dayBeforeReminder?.offsetMinutes ?? 24 * 60) / 60,
                        )
                        const total = hours * 60 + value
                        if (total < REMINDER_1_MIN) {
                          setFirstReminderMinutesError('Lead time must be at least 24 hours')
                          return
                        }
                        if (total > REMINDER_1_MAX) {
                          setFirstReminderMinutesError('Lead time cannot exceed 30 days')
                          return
                        }
                        setFirstReminderMinutesError(null)
                        form.setFieldValue('appointmentReminders.dayBeforeReminder.offsetMinutes', Math.round(total))
                        return
                      }

                      if (firstReminderMinutesError) {
                        return
                      }

                      setFirstReminderMinutesError(null)
                      const hours = Math.floor(
                        (form.values.appointmentReminders?.dayBeforeReminder?.offsetMinutes ?? 24 * 60) / 60,
                      )
                      form.setFieldValue('appointmentReminders.dayBeforeReminder.offsetMinutes', hours * 60)
                    }}
                  />
                  <Text size="sm" c="dimmed" pb={8} style={{ alignSelf: 'flex-end' }}>
                    Recommended: 24 hours
                  </Text>
                </Group>

                <Text size="sm" fw={500} mt="md">
                  Reminder 2
                </Text>
                <Text size="xs" c="dimmed">
                  Time before appointment the second reminder message should be sent
                </Text>
                <Group gap="sm" align="flex-start">
                  <NumberInput
                    label="Hours"
                    min={0}
                    max={10}
                    w={110}
                    styles={{ input: { textAlign: 'center' } }}
                    error={sameDayReminderHoursError}
                    value={sameDayHoursFloor}
                    onChange={value => {
                      if (sameDayReminderMinutesError) {
                        return
                      }

                      if (typeof value === 'number' && !Number.isNaN(value)) {
                        if (value < 0) {
                          setSameDayReminderHoursError('Hours cannot be negative')
                          return
                        }
                        if (value > 10) {
                          setSameDayReminderHoursError('Hours cannot be more than 10')
                          return
                        }
                        if (!Number.isInteger(value)) {
                          setSameDayReminderHoursError('Hours must be a whole number')
                          return
                        }
                        const minutes = (form.values.appointmentReminders?.hourBeforeReminder?.offsetMinutes ?? 60) % 60
                        const total = value * 60 + minutes
                        if (total > REMINDER_2_MAX) {
                          setSameDayReminderHoursError('Lead time cannot exceed 10 hours')
                          return
                        }
                        setSameDayReminderHoursError(null)
                        form.setFieldValue(
                          'appointmentReminders.hourBeforeReminder.offsetMinutes',
                          Math.max(0, Math.round(total)),
                        )
                        return
                      }

                      if (sameDayReminderHoursError) {
                        return
                      }

                      setSameDayReminderHoursError(null)
                      const hours = 0
                      const minutes = (form.values.appointmentReminders?.hourBeforeReminder?.offsetMinutes ?? 60) % 60
                      const total = hours * 60 + minutes
                      form.setFieldValue('appointmentReminders.hourBeforeReminder.offsetMinutes', Math.max(0, Math.round(total)))
                    }}
                  />
                  <NumberInput
                    label="Minutes"
                    min={0}
                    max={sameDayMinutesMax}
                    w={110}
                    styles={{ input: { textAlign: 'center' } }}
                    error={sameDayReminderMinutesError}
                    value={(form.values.appointmentReminders?.hourBeforeReminder?.offsetMinutes ?? 60) % 60}
                    onChange={value => {
                      if (sameDayReminderHoursError) {
                        return
                      }

                      if (typeof value === 'number' && !Number.isNaN(value)) {
                        if (value < 0 || value > sameDayMinutesMax) {
                          setSameDayReminderMinutesError(
                            sameDayMinutesMax < 59
                              ? `Minutes must be between 0 and ${sameDayMinutesMax} at this hour count`
                              : 'Minutes must be between 0 and 59',
                          )
                          return
                        }
                        if (!Number.isInteger(value)) {
                          setSameDayReminderMinutesError('Minutes must be a whole number')
                          return
                        }
                        const hours = Math.floor((form.values.appointmentReminders?.hourBeforeReminder?.offsetMinutes ?? 60) / 60)
                        const total = hours * 60 + value
                        if (total > REMINDER_2_MAX) {
                          setSameDayReminderMinutesError('Lead time cannot exceed 10 hours')
                          return
                        }
                        setSameDayReminderMinutesError(null)
                        form.setFieldValue(
                          'appointmentReminders.hourBeforeReminder.offsetMinutes',
                          Math.max(0, Math.round(total)),
                        )
                        return
                      }

                      if (sameDayReminderMinutesError) {
                        return
                      }

                      setSameDayReminderMinutesError(null)
                      const minutes = 0
                      const hours = Math.floor((form.values.appointmentReminders?.hourBeforeReminder?.offsetMinutes ?? 60) / 60)
                      const total = hours * 60 + minutes
                      form.setFieldValue('appointmentReminders.hourBeforeReminder.offsetMinutes', Math.max(0, Math.round(total)))
                    }}
                  />
                  <Text size="sm" c="dimmed" pb={8} style={{ alignSelf: 'flex-end' }}>
                    Recommended: 1 hour
                  </Text>
                </Group>

                <Divider my="md" />

                <Group justify="space-between" align="flex-start" wrap="wrap" gap="md" mb="xs">
                  <div>
                    <Text size="sm" fw={500}>
                      Booking-to-Reminder Proximity Threshold
                    </Text>
                    <Text size="xs" c="dimmed" maw={520}>
                      Prevents scheduled reminders from sending if the appointment was booked too close to the reminder send time
                    </Text>
                  </div>
                  <Switch
                    size="md"
                    onLabel="ON"
                    offLabel="OFF"
                    checked={proximityTimeEnabled}
                    onChange={event => {
                      form.setFieldValue('appointmentReminders.proximityTime.enabled', event.currentTarget.checked)
                      if (!event.currentTarget.checked) {
                        setProximityHoursError(null)
                        setProximityMinutesError(null)
                      }
                    }}
                  />
                </Group>
                <div style={{ opacity: proximityTimeEnabled ? 1 : 0.55 }}>
                  <Group gap="sm" align="flex-start">
                    <NumberInput
                      label="Hours"
                      min={0}
                      w={110}
                      styles={{ input: { textAlign: 'center' } }}
                      error={proximityHoursError}
                      disabled={!proximityTimeEnabled}
                      value={proximityHoursFloor}
                      onChange={value => {
                        if (proximityMinutesError) {
                          return
                        }

                        if (typeof value === 'number' && !Number.isNaN(value)) {
                          if (value < 0) {
                            setProximityHoursError('Hours cannot be negative')
                            return
                          }
                          if (!Number.isInteger(value)) {
                            setProximityHoursError('Hours must be a whole number')
                            return
                          }
                          const minutes =
                            (form.values.appointmentReminders?.proximityTime?.offsetMinutes ?? PROXIMITY_DEFAULT) % 60
                          const total = value * 60 + minutes
                          if (total < 1) {
                            setProximityHoursError('Threshold must be at least 1 minute')
                            return
                          }
                          setProximityHoursError(null)
                          form.setFieldValue('appointmentReminders.proximityTime.offsetMinutes', Math.max(1, Math.round(total)))
                          return
                        }

                        if (proximityHoursError) {
                          return
                        }

                        setProximityHoursError(null)
                        const minutes = (form.values.appointmentReminders?.proximityTime?.offsetMinutes ?? PROXIMITY_DEFAULT) % 60
                        const total = 0 * 60 + minutes
                        form.setFieldValue('appointmentReminders.proximityTime.offsetMinutes', Math.max(1, Math.round(total)))
                      }}
                    />
                    <NumberInput
                      label="Minutes"
                      min={0}
                      max={proximityMinutesMax}
                      w={110}
                      styles={{ input: { textAlign: 'center' } }}
                      error={proximityMinutesError}
                      disabled={!proximityTimeEnabled}
                      value={proximityOffsetMinutes % 60}
                      onChange={value => {
                        if (proximityHoursError) {
                          return
                        }

                        if (typeof value === 'number' && !Number.isNaN(value)) {
                          if (value < 0 || value > proximityMinutesMax) {
                            setProximityMinutesError(
                              proximityMinutesMax < 59
                                ? `Minutes must be between 0 and ${proximityMinutesMax} at this hour count`
                                : 'Minutes must be between 0 and 59',
                            )
                            return
                          }
                          if (!Number.isInteger(value)) {
                            setProximityMinutesError('Minutes must be a whole number')
                            return
                          }
                          const hours = Math.floor(
                            (form.values.appointmentReminders?.proximityTime?.offsetMinutes ?? PROXIMITY_DEFAULT) / 60,
                          )
                          const total = hours * 60 + value
                          if (total < 1) {
                            setProximityMinutesError('Threshold must be at least 1 minute')
                            return
                          }
                          setProximityMinutesError(null)
                          form.setFieldValue('appointmentReminders.proximityTime.offsetMinutes', Math.round(total))
                          return
                        }

                        if (proximityMinutesError) {
                          return
                        }

                        setProximityMinutesError(null)
                        const hours = Math.floor(
                          (form.values.appointmentReminders?.proximityTime?.offsetMinutes ?? PROXIMITY_DEFAULT) / 60,
                        )
                        const total = hours * 60
                        form.setFieldValue(
                          'appointmentReminders.proximityTime.offsetMinutes',
                          Math.max(1, Math.round(total || 1)),
                        )
                      }}
                    />
                    <Text size="sm" c="dimmed" pb={8} style={{ alignSelf: 'flex-end' }}>
                      Recommended: 2 hours
                    </Text>
                  </Group>
                </div>
              </Stack>
            )}
          </Stack>
        </Card>
      </Stack>
    </form>
  )
}
